import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SafeIcon from "../../common/SafeIcon";
import * as FiIcons from "react-icons/fi";
import { useClient } from "../../context/ClientContext";
import { clientReportService } from "../../services/client/clientReportService";
import ClientReportSelector from "../../components/clients/reports/ClientReportSelector";
import ClientReportFilters from "../../components/clients/reports/ClientReportFilters";
import ClientReportViewer from "../../components/clients/reports/ClientReportViewer";
import { downloadClientReportCsv } from "../../components/clients/reports/ClientReportExport";
import ClientReportPreviewModal from "../../components/clients/modals/ClientReportPreviewModal";

const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const yearStart = () => `${new Date().getFullYear()}-01-01`;

function money(value, currency) {
  const amount = Number(value || 0);
  try {
    const formatted = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(Math.abs(amount));
    return amount < 0 ? `(${formatted})` : formatted;
  } catch {
    return amount.toFixed(2);
  }
}

function statementRows(data, type, accountId, page, pageSize, search) {
  if (type === "profitLoss") {
    const rows = [];
    const sections = [
      ["income", "Income", "Total Income"],
      ["costOfGoodsSold", "Cost of Goods Sold", "Total Cost of Goods Sold"],
      ["expenses", "Operating Expenses", "Total Operating Expenses"],
    ];
    sections.forEach(([key, title, totalLabel]) => {
      rows.push({ key: `${key}-heading`, label: title, amount: "", kind: "section" });
      (data.accountDetails?.[key] || []).forEach((account) =>
        rows.push({ key: account.id, label: `${account.code ? `${account.code} · ` : ""}${account.name}`, amount: account.amount, level: 1 }),
      );
      rows.push({ key: `${key}-total`, label: totalLabel, amount: data[key].total, kind: "total" });
    });
    rows.push({ key: "gross-profit", label: "Gross Profit", amount: data.grossProfit, kind: "total" });
    rows.push({ key: "net-income", label: "Net Profit (Loss)", amount: data.netIncome, kind: "grand" });
    return {
      title: "Profit & Loss",
      period: `${data.metadata?.startDate || ""} to ${data.metadata?.endDate || ""}`,
      columns: [{ key: "label", label: "Account" }, { key: "amount", label: "Amount", numeric: true }],
      rows,
    };
  }

  if (type === "balanceSheet") {
    const rows = [];
    [["assets", "Assets"], ["liabilities", "Liabilities"], ["equity", "Equity"]].forEach(([section, title]) => {
      rows.push({ key: `${section}-heading`, label: title, amount: "", kind: "section" });
      Object.entries(data[section]?.categories || {}).forEach(([category, values]) => {
        rows.push({ key: `${section}-${category}`, label: category, amount: values.total, kind: "total" });
        (values.accountDetails || []).forEach((account) =>
          rows.push({ key: account.id, label: `${account.code ? `${account.code} · ` : ""}${account.name}`, amount: account.amount, level: 1 }),
        );
      });
      rows.push({ key: `${section}-total`, label: `Total ${title}`, amount: data[section].total, kind: "grand" });
    });
    const liabilitiesEquity = Number(data.liabilities.total || 0) + Number(data.equity.total || 0);
    const variance = Number(data.assets.total || 0) - liabilitiesEquity;
    return {
      title: "Balance Sheet",
      period: `As of ${data.asOfDate}`,
      columns: [{ key: "label", label: "Account / Category" }, { key: "amount", label: "Balance", numeric: true }],
      rows: [
        ...rows,
        { key: "liabilities-equity", label: "Total Liabilities & Equity", amount: liabilitiesEquity, kind: "grand" },
      ],
      warnings: Math.abs(variance) >= 0.01 ? [`Balance Sheet variance: ${money(variance, "USD")}. The report is not balanced; no adjustment has been made.`] : [],
    };
  }

  if (type === "trialBalance") {
    const balanced = Math.abs(data.difference) < 0.01;
    return {
      title: "Trial Balance",
      period: `As of ${data.asOfDate}`,
      columns: [
        { key: "code", label: "Account Code" },
        { key: "name", label: "Account Name" },
        { key: "debit", label: "Debit", numeric: true },
        { key: "credit", label: "Credit", numeric: true },
      ],
      rows: [
        ...data.rows.map((row) => ({ ...row, key: row.id })),
        { key: "totals", code: "", name: "Total", debit: data.totalDebit, credit: data.totalCredit, kind: "grand" },
      ],
      warnings: balanced ? [] : [`Trial Balance difference is ${money(data.difference, "USD")}. No adjustment entry has been created.`],
    };
  }

  if (type === "generalLedger") {
    const start = (page - 1) * pageSize;
    const rows = data.rows.map((row) => ({
      key: row.id,
      date: row.date,
      reference: row.reference,
      description: row.description || "—",
      debit: row.debit,
      credit: row.credit,
      balance: row.runningBalance,
    }));
    return {
      title: `General Ledger · ${data.account.name}`,
      period: `${data.startDate || "All dates"} to ${data.endDate || "Current date"}`,
      columns: [
        { key: "date", label: "Date" },
        { key: "reference", label: "Reference" },
        { key: "description", label: "Description" },
        { key: "debit", label: "Debit", numeric: true },
        { key: "credit", label: "Credit", numeric: true },
        { key: "balance", label: "Running Balance", numeric: true },
      ],
      rows: [
        { key: "opening", date: "", reference: "", description: "Opening Balance", debit: "", credit: "", balance: data.openingBalance, kind: "total" },
        ...rows,
        { key: "closing", date: "", reference: "", description: "Closing Balance", debit: "", credit: "", balance: data.closingBalance, kind: "grand" },
      ],
      pageInfo: `Showing ${Math.min(start + 1, data.totalRows)}–${Math.min(start + pageSize, data.totalRows)} of ${data.totalRows} matching rows`,
      pagination: {
        page,
        pageCount: Math.max(1, Math.ceil(data.totalRows / pageSize)),
      },
    };
  }

  return null;
}

export default function ClientReports() {
  const { activeWorkspace } = useClient();
  const organizationId = activeWorkspace?.id || null;
  const organizationName = activeWorkspace?.name || "Your business";
  const currency = String(activeWorkspace?.currency || "USD").toUpperCase();
  const requestRef = useRef(0);

  const [reportType, setReportType] = useState("profitLoss");
  const [dates, setDates] = useState({ startDate: yearStart(), endDate: today() });
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const pageSize = 25;

  useEffect(() => {
    const requestId = ++requestRef.current;
    let current = true;

    setReport(null);
    setError("");
    setPreviewOpen(false);
    setAccountId("");
    setPage(1);

    if (!organizationId) {
      setAccounts([]);
      setLoadingAccounts(false);
      return () => {
        current = false;
      };
    }

    setLoadingAccounts(true);
    setAccounts([]);
    clientReportService.getClientReportAccounts(organizationId)
      .then((result) => {
        if (current && requestRef.current === requestId) setAccounts(result);
      })
      .catch((loadError) => {
        if (current && requestRef.current === requestId) {
          setError(loadError?.message || "Unable to load report accounts.");
        }
      })
      .finally(() => {
        if (current && requestRef.current === requestId) setLoadingAccounts(false);
      });

    return () => {
      current = false;
    };
  }, [organizationId]);

  useEffect(() => {
    setReport(null);
    setError("");
    setPreviewOpen(false);
    setPage(1);
  }, [reportType, dates, accountId, search]);

  const generate = useCallback(async () => {
    if (!organizationId) {
      setError("Select a business before generating a report.");
      return;
    }

    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    setReport(null);

    try {
      let data;
      if (reportType === "profitLoss") {
        data = await clientReportService.getClientProfitLoss({
          organizationId,
          ...dates,
        });
      } else if (reportType === "balanceSheet") {
        data = await clientReportService.getClientBalanceSheet({
          organizationId,
          asOfDate: dates.endDate,
        });
        data.asOfDate = dates.endDate;
      } else if (reportType === "trialBalance") {
        data = await clientReportService.getClientTrialBalance({
          organizationId,
          asOfDate: dates.endDate,
        });
      } else if (reportType === "generalLedger") {
        data = await clientReportService.getClientGeneralLedger({
          organizationId,
          accountId,
          startDate: dates.startDate,
          endDate: dates.endDate,
          search,
          page,
          pageSize,
        });
        data.startDate = dates.startDate;
        data.endDate = dates.endDate;
      } else {
        data = await clientReportService.getClientCashFlow();
      }

      if (requestRef.current !== requestId) return;
      const model = data.supported === false
        ? data
        : statementRows(data, reportType, accountId, page, pageSize, search);
      setReport(model);
    } catch (loadError) {
      if (requestRef.current === requestId) {
        setError(loadError?.message || "Unable to generate this report.");
      }
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [accountId, dates, organizationId, page, pageSize, reportType, search]);

  const exportReport = useCallback(() => {
    try {
      downloadClientReportCsv(report, organizationName);
    } catch (exportError) {
      setError(exportError.message || "Unable to export this report.");
    }
  }, [organizationName, report]);

  const updateDates = (next) => setDates(next);
  const updatePage = (next) => setPage(next);

  const reportTitle = useMemo(() => report?.title || "", [report]);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-violet-700">Financial management</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Financial Reports</h1>
          <p className="mt-2 text-sm text-slate-600">View and analyze your business&apos;s financial performance.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Selected business</p>
          <p className="mt-1 max-w-xs truncate text-sm font-bold text-slate-800">{organizationName}</p>
        </div>
      </header>

      <ClientReportSelector value={reportType} onChange={setReportType} />

      <ClientReportFilters
        reportType={reportType}
        dates={dates}
        accounts={accounts}
        accountId={accountId}
        page={page}
        search={search}
        loading={loading || loadingAccounts}
        onDatesChange={updateDates}
        onAccountChange={setAccountId}
        onPageChange={updatePage}
        onSearchChange={setSearch}
        onGenerate={generate}
      />

      {(error || loadingAccounts) && (
        <div role={error ? "alert" : undefined} className={`rounded-xl border px-4 py-3 text-sm ${error ? "border-rose-200 bg-rose-50 text-rose-800" : "border-slate-200 bg-white text-slate-500"}`}>
          {error || "Loading organization accounts…"}
        </div>
      )}

      <ClientReportViewer
        report={report}
        loading={loading}
        error=""
        currency={currency}
        onPreview={() => setPreviewOpen(true)}
        onExport={exportReport}
      />

      {report?.pagination && (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
          <span>{report.pageInfo}</span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))} className="rounded-lg border border-slate-200 px-3 py-2 font-semibold disabled:opacity-40">Previous</button>
            <span className="px-2 py-2">Page {page} of {report.pagination.pageCount}</span>
            <button type="button" disabled={!report.pagination || page >= report.pagination.pageCount || loading} onClick={() => setPage((current) => current + 1)} className="rounded-lg border border-slate-200 px-3 py-2 font-semibold disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {reportType === "trialBalance" && report?.warnings?.length === 0 && (
        <p className="flex items-center gap-2 text-xs font-medium text-emerald-700">
          <SafeIcon icon={FiIcons.FiCheckCircle} /> Trial Balance debits and credits agree.
        </p>
      )}

      <ClientReportPreviewModal
        report={previewOpen ? report : null}
        businessName={organizationName}
        currency={currency}
        onClose={() => setPreviewOpen(false)}
        onExport={exportReport}
      />
      {reportTitle && <span className="sr-only">{reportTitle}</span>}
    </div>
  );
}